// @ts-strict-ignore
import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, Input, model, OnInit, output, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ProjectComponentPresenter } from 'app/modules/project/components/project-component.presenter';
import { ProjectHistoryActionDef } from 'app/modules/project/components/project/constants/project-history-action-def';
import {
  ProjectVersionHistoryDropdownMenuComponent
} from 'app/modules/project/components/project/project-version-history/project-version-history-dropdown-menu/project-version-history-dropdown-menu.component';
import { ProjectUtils } from 'app/modules/project/components/project/util/project-utils';
import { ProjectHistoryService } from 'app/modules/project/history/project-history.service';
import { ProjectHeadVO, ProjectHistoryEntryVO, ProjectHistoryVO } from 'app/modules/project/model/vo/project.vo';
import { DateFormatter } from 'app/modules/shared/date-formatter';
import { Log } from 'app/modules/shared/debug/log';
import { FixedHeaderDirective } from 'app/modules/shared/directives/fixed-header.directive';
import { SvgIconName } from 'app/modules/shared/svg-icon/svg-icon-name';
import { SvgIconComponent } from 'app/modules/shared/svg-icon/svg-icon.component';
import { SystemConfigurationService } from 'app/modules/shared/system-configuration/system-configuration-loading.service';
import { TooltipDirective } from 'app/modules/shared/tooltip/tooltip.directive';
import { NumberUtils } from 'app/modules/shared/util/number-utils';
import { OrderByPipe } from 'app/modules/shared/util/order-by.pipe';
import { UserSettings } from 'app/modules/user/model/user-settings';
import { UserModel } from 'app/modules/user/model/user.model';
import { UserUtils } from 'app/modules/user/service/user-utils';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NgScrollbar } from 'ngx-scrollbar';

export type ProjectHistoryTableEntry = {
  key: string,
  projectId: number,
  projectVersion: number,
  projectVersionSaveDate: Date,
  creationDateServerTimeStamp: string,
  projectVersionLabel: string,
  username: string,
  remark: string,
  date: Date,
  dateLabel: string,
  finalizationStateIcon: SvgIconName,
  finalizationStateTooltip: string,
  priceCalculationRefId: string,
  projectVersionDisplayString: string,
  isAvailable: boolean,
}

@Component({
  selector: 'app-project-version-history-table',
  imports: [
    SvgIconComponent,
    TooltipDirective,
    TranslateModule,
    ProjectVersionHistoryDropdownMenuComponent,
    OrderByPipe,
    FixedHeaderDirective,
    NgScrollbar
  ],
  templateUrl: './project-version-history-table.component.html',
  styleUrls: ['./project-version-history-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectVersionHistoryTableComponent implements OnInit {
  private readonly projectHistoryService = inject(ProjectHistoryService);
  private readonly bsModalRef = inject(BsModalRef);
  private readonly userSettings = inject(UserSettings);
  private readonly userModel = inject(UserModel);
  private readonly systemConfigurationService = inject(SystemConfigurationService);
  private readonly projectComponentPresenter = inject(ProjectComponentPresenter);

  public projectHistory = input.required<ProjectHistoryVO>();
  public project = input.required<ProjectHeadVO>();
  public preselectVersion = input<number>();
  public showPriceCalculationLink = UserUtils.hasAccessPriceCalculationPermission(this.userModel.user)

  static readonly DATE_PROPERTY: string = 'date';
  static readonly PROJECT_VERSION_DISPLAY_STRING_PROPERTY: string = 'projectVersionDisplayString';

  public selectedEntry = model<ProjectHistoryTableEntry | undefined>(undefined);
  public selectedFilter = model<string | undefined>(undefined);

  projectHistoryVersions = computed(() => {
    return new Set(this.projectHistory().projectHistoryEntries.map((entry) => entry.projectVersionDisplayString))
  });

  public versionLoadedByTemporaryLink = computed(() => {
    if (this.project().temporaryLinkGUID) {
      return this.project().currentVersion;
    }
  });

  private readonly mappedEntries = computed<ProjectHistoryTableEntry[]>(() =>
    this.projectHistory().projectHistoryEntries.map((historyEntry: ProjectHistoryEntryVO) => ({
      key: `${historyEntry.projectVersion}_${historyEntry.action}_${new Date(historyEntry.date).getTime()}`,
      projectId: historyEntry.projectId,
      projectVersion: historyEntry.projectVersion,
      projectVersionSaveDate: historyEntry.projectVersionSaveDate,
      creationDateServerTimeStamp: historyEntry.creationDateServerTimeStamp,
      projectVersionLabel: this.getProjectVersionLabel(historyEntry),
      username: this.projectHistoryService.getDisplayUsername(historyEntry.modifiedBy),
      remark: this.getRemarkLabel(historyEntry),
      date: historyEntry.date,
      dateLabel: this.getDateLabel(historyEntry),
      finalizationStateIcon: this.getFinalizationStateIcon(historyEntry),
      finalizationStateTooltip: this.projectHistoryService.getFinalizationStateTooltip(historyEntry),
      priceCalculationRefId: historyEntry.priceCalculationRefId,
      projectVersionDisplayString: historyEntry.projectVersionDisplayString,
      isAvailable: NumberUtils.isNumber(this.versionLoadedByTemporaryLink())
        ? historyEntry.projectVersion === this.versionLoadedByTemporaryLink()
        : true,
    }))
  );

  public tableEntries = computed<ProjectHistoryTableEntry[]>(() => {
    const filter = this.selectedFilter();
    const entries = this.mappedEntries();
    return filter ? entries.filter((entry) => entry.projectVersionDisplayString === filter) : entries;
  });

  @Input() compareItemListMode: boolean = false;
  @Output() public finish = new EventEmitter<boolean | undefined>();

  public historyLoaded = output<ProjectHistoryVO>();

  public update = new EventEmitter<void>();

  public orderAttribute: string;
  public sortAsc: boolean;
  public isConstruction = false;

  public ngOnInit(): void {
    this.orderAttribute = (this.userSettings.historySortAttribute ? this.userSettings.historySortAttribute : ProjectVersionHistoryTableComponent.DATE_PROPERTY) as keyof ProjectHistoryTableEntry;
    this.sortAsc = this.userSettings.historySortOrder;
    this.isConstruction = this.userModel.isConstruction && (!this.userModel.user || this.userModel.user.isProduction());
    this.preselect();
  }

  private preselect(): void {
    if (this.preselectVersion() !== undefined) {
      const foundVersion = this.tableEntries().find((entry) => entry.projectVersion === this.preselectVersion());
      this.setEntry(foundVersion);
    }
  }

  public setEntry(selected: ProjectHistoryTableEntry): void {
    this.selectedEntry.set(selected);
  }

  public onCancel(projectLoaded?: boolean): void {
    this.bsModalRef.hide();
    this.finish.emit(projectLoaded);
  }

  private getFinalizationStateIcon(historyEntry: ProjectHistoryEntryVO): SvgIconName {
    return ProjectUtils.getFinalizationStateIconByFinalizationStateAndDocumentVersion(
      historyEntry.finalizationState,
      historyEntry.documentVersion,
      historyEntry.action
    );
  }

  private getProjectVersionLabel(historyEntry: ProjectHistoryEntryVO): string {
    if (!historyEntry) {
      return '';
    }
    const versionLabel: string = historyEntry.projectVersionDisplayString;
    if (historyEntry.documentVersion > -1 && UserUtils.isWagoUser(this.userModel.user)) {
      return (versionLabel ? versionLabel : '-') + ' / ' + historyEntry.documentVersion;
    } else {
      return versionLabel;
    }
  }

  private getRemarkLabel(historyEntry: ProjectHistoryEntryVO): string {
    if (!historyEntry) {
      return '';
    }

    const action: number = historyEntry.action;
    if (action === ProjectHistoryActionDef.CHANGED_FINALIZATION_STATE) {
      return 'projectVersionHistoryPopup.label.remark.changed_finalization_state';
    } else if (action === ProjectHistoryActionDef.CREATED_NEW_PROJECT_VERSION) {
      return 'projectVersionHistoryPopup.label.remark.created_new_project_version';
    } else if (action === ProjectHistoryActionDef.CREATED_PROJECT) {
      return 'projectVersionHistoryPopup.label.remark.created_project';
    } else if (action === ProjectHistoryActionDef.EDITED_PROJECT) {
      return 'projectVersionHistoryPopup.label.remark.edited_project';
    } else if (action === ProjectHistoryActionDef.FINALIZED_LEGACY_PROJECT) {
      return 'projectVersionHistoryPopup.label.remark.finalized_legacy_project';
    } else if (action === ProjectHistoryActionDef.FINALIZED_PROJECT) {
      return 'projectVersionHistoryPopup.label.remark.finalized_project';
    } else if (action === ProjectHistoryActionDef.FINALIZED_PROJECT_WITHOUT_PDM) {
      return 'projectVersionHistoryPopup.label.remark.finalized_project_without_pdm';
    } else if (action === ProjectHistoryActionDef.ORDERED_PROJECT) {
      return 'projectVersionHistoryPopup.label.remark.ordered_project';
    } else if (action === ProjectHistoryActionDef.REQUESTED_PROJECT) {
      return 'projectVersionHistoryPopup.label.remark.requested_project';
    } else if (action === ProjectHistoryActionDef.LOCKED_PROJECT) {
      return 'projectVersionHistoryPopup.label.remark.locked_project';
    } else if (action === ProjectHistoryActionDef.UNLOCKED_PROJECT) {
      return 'projectVersionHistoryPopup.label.remark.unlocked_project';
    } else {
      Log.error('Translation key not defined for ProjectHistoryAction:  ' + action);
      return '';
    }
  }

  private getDateLabel(historyEntry: ProjectHistoryEntryVO): string {
    if (!historyEntry || !historyEntry.date) {
      return '';
    }
    return DateFormatter.formatDate(historyEntry.date);
  }

  public copyProjectUrl(historyEntry: ProjectHistoryTableEntry): void {
    this.projectComponentPresenter.openShareProjectLinkPopup(
      historyEntry.projectId,
      historyEntry.projectVersion,
      historyEntry.creationDateServerTimeStamp,
      this.projectHistory().articleNumberLong,
      this.projectHistory().projectName
    );
  }

  public isPriceCalculationAvailable(historyEntry: ProjectHistoryTableEntry): boolean {
    return UserUtils.canSeePriceCalculationIcon(this.userModel.user) && !!historyEntry.priceCalculationRefId;
  }

  public openPriceCalculationLookup(historyEntry: ProjectHistoryTableEntry): void {
    const lookupUrl: string = this.systemConfigurationService.currentConfiguration.priceCalculationLookupURL + historyEntry.priceCalculationRefId;
    window.open(lookupUrl, '_blank');
  }

  private changeOrderBy(orderArttibute: string): void {
    if (this.orderAttribute === orderArttibute) {
      this.sortAsc = !this.sortAsc;
    }
    this.orderAttribute = orderArttibute;
    this.userSettings.setUserSettingValue("historySortAttribute", this.orderAttribute);
    this.userSettings.setUserSettingValue("historySortOrder", this.sortAsc);
    this.userSettings.saveUserSettings();
  }

  public changeOrderToDate(): void {
    this.changeOrderBy(ProjectVersionHistoryTableComponent.DATE_PROPERTY);
  }

  public getVersionIcon(): string {
    return this.getIcon(ProjectVersionHistoryTableComponent.PROJECT_VERSION_DISPLAY_STRING_PROPERTY);
  }

  public getDateIcon(): string {
    return this.getIcon(ProjectVersionHistoryTableComponent.DATE_PROPERTY);
  }

  private getIcon(sortValue: string): string {
    if (this.orderAttribute === sortValue) {
      if (this.sortAsc) {
        return 'mdi material-icons md-18 c-pointer mdi-menu-up';
      } else {
        return 'mdi material-icons md-18 c-pointer mdi-menu-down';
      }
    } else {
      return 'mdi mdi-menu-swap color-gray-500 material-icons md-18 c-pointer';
    }
  }

  public changeOrderToProjectVersion(): void {
    this.changeOrderBy(ProjectVersionHistoryTableComponent.PROJECT_VERSION_DISPLAY_STRING_PROPERTY);
  }
}
